import { get, groupBy, pickBy, countBy, omit } from 'lodash-es'
import { getLeafIds } from '@/utils/getLeafIds'
import { getModelAdapter, getModelCategories, getModelAdapters } from './modelAdapterRegistry'
import { getChemicalUniqueKey } from '@/utils/chemical'
import { isNil, sortBy } from 'lodash-es'
import { buildGroupColumnDef } from '@/components/shared-table/buildColumnDef'
import { buildMappedGroup } from '@/components/combined-predict/components/CombinedPredictTable/utils/mappedGroupBuilders'
import { mapUnitOrOriginal } from '@/components/combined-predict/components/CombinedPredictTable/utils/unitConversions'
import { buildAggregateColumnDef } from '@/components/combined-predict/components/CombinedPredictTable/utils/regroupBuilders'

const getModelNameById = (modelId) => {
  const adapter = getModelAdapter(modelId)
  return adapter?.getName?.() ?? modelId
}

const getCategoryIdByModelId = (modelId) => {
  const adapter = getModelAdapter(modelId)
  return adapter?.categoryId ?? null
}

const getCategoryNameByModelId = (modelId) => {
  const categoryId = getCategoryIdByModelId(modelId)
  const category = getModelCategories().find((category) => category.id === categoryId)
  return category?.name ?? null
}

// Combined Progress loading and aborted
const getModelIsLoading = (modelId) => {
  const adapter = getModelAdapter(modelId)
  if (!adapter?.getIsLoading) return false
  return adapter.getIsLoading()
}

const getModelProgress = (modelId) => {
  const adapter = getModelAdapter(modelId)
  if (isNil(adapter?.getProgress)) return null
  return adapter.getProgress()
}

const getModelProgressLabel = (modelId) => {
  const adapter = getModelAdapter(modelId)
  if (!adapter?.getProgressLabel) return null
  return adapter.getProgressLabel()
}

const getCombinedProgress = (activeModelIds) => {
  const values = activeModelIds
    .map((modelId) => {
      const progress = getModelProgress(modelId)
      if (progress === null) {
        return getModelIsLoading(modelId) ? 0 : 100
      }

      return progress
    })
    .filter((value) => typeof value === 'number' && !Number.isNaN(value))

  if (!values.length) return 0
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)
}

const getCombinedIsLoading = (activeModelIds) =>
  activeModelIds.some(
    (modelId) => (getModelProgress(modelId) !== null && getModelProgress(modelId) < 100) || getModelIsLoading(modelId),
  )

const getCombinedIsAborted = (activeModelIds) =>
  activeModelIds.some((modelId) => Boolean(getModelAdapter(modelId)?.getIsAborted?.()))

const getCombinedProgressLabels = (activeModelIds) =>
  (activeModelIds ?? [])
    .map((modelId) => {
      const label = getModelProgressLabel(modelId)
      if (!label) return null
      const name = getModelNameById(modelId)
      return `${name}: ${label}`
    })
    .filter(Boolean)

// Logic to get CombinedRows
const extractChemicalFromResult = (result, modelId) => {
  const adapter = getModelAdapter(modelId)
  if (!adapter) return null
  return get(result, adapter.chemicalKey)
}

const extractPredictionsFromResult = (result, modelId) => {
  const adapter = getModelAdapter(modelId)
  if (!adapter) return null
  return adapter?.predictionsKey ? get(result, adapter?.predictionsKey) : result
}

const extractErrorFromResult = (result, modelId) => {
  const adapter = getModelAdapter(modelId)
  if (!adapter?.errorKey) return null
  // console.log(`Extracting error for model ${modelId} using key ${adapter.errorKey}:`, get(result, adapter.errorKey))
  // console.log(`Extracting error for model ${modelId} using key ${adapter.errorKey}:`, get(result, adapter.errorKey))
  return get(result, adapter.errorKey)
}

const getModelErrorFormatter = (modelId) => {
  const adapter = getModelAdapter(modelId)
  if (!adapter?.formatError) return (rawError) => rawError
  return adapter.formatError
}

const formatModelError = (modelId, rawError) => {
  const formatter = getModelErrorFormatter(modelId)
  return formatter(rawError)
}

const getModelResults = (modelId) => {
  const adapter = getModelAdapter(modelId)

  if (!adapter?.getResults) return []
  const results = adapter.getResults()
  if (!results || !Array.isArray(results)) {
    // console.warn(
    //   'No results found or results returned in unexpected format for model',
    //   modelId,
    //   '. Adapter getResults returned:',
    //   results,
    // )
    return []
  }
  return results
}

const extractResultExtras = (result, modelId) => {
  const adapter = getModelAdapter(modelId)
  if (!adapter || !result || typeof result !== 'object') return {}
  const { chemicalKey, predictionsKey, errorKey } = adapter

  return {
    ...omit(result, [chemicalKey, predictionsKey, errorKey]),
  }
}

const collectModelResults = (modelIds) => {
  if (!modelIds?.length) return []
  return modelIds.flatMap((modelId) => {
    return getModelResults(modelId).map((result) => ({
      ...extractResultExtras(result, modelId),
      modelId,
      chemical: extractChemicalFromResult(result, modelId),
      predictions: extractPredictionsFromResult(result, modelId),
      error: formatModelError(modelId, extractErrorFromResult(result, modelId)),
    }))
  })
}

const buildCombinedRows = (activeModelIds) => {
  const allResults = collectModelResults(activeModelIds)
  if (!allResults?.length) return []

  const resultsGroupedByChemical = groupBy(
    allResults,
    (result) => result.chemical?.id || getChemicalUniqueKey(result.chemical),
  )

  const reduced = Object.values(resultsGroupedByChemical).map((chemicalResult) =>
    chemicalResult.reduce(
      (acc, { chemical, modelId, predictions, error, ...extras }) => {
        if (!acc.chemical) acc.chemical = chemical
        acc.predictionsByModelId[modelId] = predictions ?? []
        acc.errorsByModelId[modelId] = error ?? null
        acc.extrasByModelId[modelId] = extras ?? {}
        return acc
      },
      {
        chemical: null,
        predictionsByModelId: {},
        errorsByModelId: {},
        extrasByModelId: {},
      },
    ),
  )

  return sortBy(reduced, (row) => row.chemical?.inputOrder) // Sort by chemical name, handling cases where chemical or name might be missing
}

// Fetching, resetting, and aborting functions
const fetchSelectedModels = ({
  modelIds,
  chemicals,
  // , onLoadingChanged
}) =>
  Promise.allSettled(
    modelIds.map((modelId) => {
      const adapter = getModelAdapter(modelId)

      if (!adapter?.fetch) return Promise.resolve()
      // onLoadingChanged?.(modelId, true)

      return adapter.fetch(chemicals)
      // .then((results) => console.log('Fetched results for model', modelId, results))
      // .finally(() => {
      //   onLoadingChanged?.(modelId, false)
      // })
    }),
  )

const resetSelectedModels = (activeModelIds) => {
  activeModelIds.forEach((modelId) => {
    const adapter = getModelAdapter(modelId)
    adapter?.reset?.()
  })
}

const abortSelectedModels = (activeModelIds) => {
  activeModelIds.forEach((modelId) => {
    const adapter = getModelAdapter(modelId)
    adapter?.abort?.()
  })
}

// Parameters
const getModelParameters = (modelId) => {
  const adapter = getModelAdapter(modelId)
  if (!adapter?.getParameters) return null
  return adapter.getParameters()
}

const getColumnDefBuilderByModel = (modelId) => {
  const adapter = getModelAdapter(modelId)
  if (!adapter?.getColumnDefBuilder) return null
  return adapter.getColumnDefBuilder
}

const getModelGroup = (modelId) => {
  const adapter = getModelAdapter(modelId)
  if (!adapter) return null

  const parameters = getModelParameters(modelId)

  if (!parameters?.length) return null

  const name = adapter.getName?.()
  const unit = adapter.getUnit?.()
  const description = adapter.getDescription?.()

  return buildMappedGroup({
    id: modelId,
    modelId,
    name,
    description,
    unit,
    children: parameters,
  })
}

const getCategorizedModels = (modelCategories, modelAdapters) => {
  const buildCategoryNode = (modelCategories, category) => {
    const children = modelCategories.filter((childCategory) => childCategory.categoryId === category.id)

    const childrenModels = Object.entries(modelAdapters ?? {})
      ?.filter(([id, adapter]) => adapter.categoryId === category.id)
      .map(([id]) => getModelGroup(id))
      .filter(Boolean)

    if (!children?.length && !childrenModels?.length) {
      return null
    }

    return {
      id: category.id,
      name: category.name,
      description: category?.description,
      children: [
        ...children.map((child) => buildCategoryNode(modelCategories, child)).filter(Boolean),
        ...childrenModels,
      ],
    }
  }

  return (
    modelCategories
      ?.filter((category) => !category.categoryId)
      .map((rootCategory) => buildCategoryNode(modelCategories, rootCategory))
      .filter(Boolean) ?? []
  )
}

const getUncategorizedModels = (modelAdapters) =>
  Object.entries(modelAdapters ?? {})
    ?.filter(([id, adapter]) => !adapter.categoryId)
    .map(([id]) => getModelGroup(id))
    .filter(Boolean) ?? []

const getCategorizedModelsWithParameters = (modelCategories, modelAdapters) => [
  ...getUncategorizedModels(modelAdapters),
  ...getCategorizedModels(modelCategories, modelAdapters),
]

const buildColumnDefsTree = ({ categories, visibleColumnIds, tableSettings }) => {
  const getColumnDefsForCategories = (categories, inheritedProps) =>
    categories.map((category) => {
      const modelId = inheritedProps?.modelId ?? category?.modelId
      const groupId = inheritedProps?.groupId ?? category?.groupId
      const memberIds = inheritedProps?.memberIds ?? category?.memberIds
      // const className = inheritedProps?.className ?? category?.className

      const originalUnit = inheritedProps?.originalUnit ?? category?.unit
      const overriddenUnit = get(tableSettings?.unitOverridesByField, category?.id)
      const pinned = get(tableSettings?.pinnedColumns, category?.id)

      const effectiveUnit = !!overriddenUnit
        ? overriddenUnit
        : inheritedProps?.unit
          ? inheritedProps.unit
          : originalUnit

      const mappedOriginalUnit = mapUnitOrOriginal(originalUnit)
      const mappedEffectiveUnit = mapUnitOrOriginal(effectiveUnit)

      const duplicateChildNames = Object.keys(
        pickBy(
          countBy(category?.children, ({ name }) => name || ''),
          (count) => count > 1,
        ),
      )

      const propagatedColumnContext = {
        unit: mappedEffectiveUnit,
        originalUnit: mappedOriginalUnit,
        modelId,
        ...(duplicateChildNames.length ? { duplicateSiblings: duplicateChildNames } : {}),
        ...(memberIds ? { memberIds } : {}),
        ...(groupId ? { groupId } : {}),
        // ...(className ? { className } : {}),
      }

      const isDuplicateSiblingName = (name) =>
        inheritedProps?.duplicateSiblings?.length && inheritedProps?.duplicateSiblings?.includes(name)

      const buildDisambiguatedName = (column) => `${column.name}${column?.sourceName ? `: ${column.sourceName}` : ''}`

      const applyShortNamePresentation = (column) => {
        if (column?.shortName)
          return {
            name: column.shortName,
            description: `${column.name}${column.description ? `: ${column.description}` : ''}`,
          }
      }

      const enrichedCategory = {
        ...category,
        ...(isDuplicateSiblingName(category?.name) ? { name: buildDisambiguatedName(category) } : {}),
        ...applyShortNamePresentation(category),
        ...inheritedProps,
        ...propagatedColumnContext,
        pinned: pinned ?? false,
      }

      return category?.children?.length
        ? buildGroupColumnDef({
            column: enrichedCategory,
            children: getColumnDefsForCategories(category?.children, propagatedColumnContext),
          })
        : modelId
          ? getColumnDefBuilderByModel(modelId)?.({
              column: enrichedCategory,
              visibleColumnIds,
              tableSettings,
            })
          : memberIds &&
            buildAggregateColumnDef({
              column: enrichedCategory,
              visibleColumnIds,
              tableSettings,
            })
    })

  return getColumnDefsForCategories(categories)
}

const getAllCategorizedModels = () => getCategorizedModelsWithParameters(getModelCategories(), getModelAdapters())
const getAllParameterIds = () => getLeafIds(getAllCategorizedModels())

const filterExistingColumnIds = (columnIds) => {
  const allParameterIds = getAllParameterIds()
  if (!allParameterIds.length) return columnIds
  return columnIds.filter((id) => allParameterIds.includes(id))
}

const isModelColumnsVisible = (visibleColumnIds, modelId) => {
  if (!visibleColumnIds?.length) return false
  const visibleExistedColumnIds = filterExistingColumnIds(visibleColumnIds)

  return visibleExistedColumnIds.some((id) => id.startsWith(modelId))
}

export {
  getModelNameById,
  getCategoryNameByModelId,
  getCategoryIdByModelId,
  getCombinedProgress,
  getCombinedIsLoading,
  getCombinedIsAborted,
  getCombinedProgressLabels,
  buildCombinedRows,
  fetchSelectedModels,
  resetSelectedModels,
  abortSelectedModels,
  buildColumnDefsTree,
  getAllCategorizedModels,
  getAllParameterIds,
  filterExistingColumnIds,
  isModelColumnsVisible,
}
